import { useEffect, useState } from 'react'
import { dates } from '@homevest/utils'
import { isDatePostable, useBuildiumBooksLockDate } from 'lib/buildiumBooksLock'

export default function useBuildiumReversalDateChoice({
  entryDate,
  isEnabled,
  isOpen,
  preferToday = false,
  rentalId
}: {
  entryDate: Date | string
  isEnabled: boolean
  isOpen: boolean
  preferToday?: boolean
  rentalId: string
}) {
  const [selectedReversalDate, setSelectedReversalDate] = useState<string | null>(null)
  const {
    data: booksLock,
    isLoading: isBooksLockLoading,
    isError: isBooksLockError
  } = useBuildiumBooksLockDate(rentalId, isOpen && isEnabled)
  const originalReversalDate = dates.normalizeToDateString(entryDate)
  const today = booksLock?.today
  const canReverseOriginal =
    isEnabled && Boolean(booksLock) && isDatePostable(originalReversalDate, booksLock?.lockDate)
  const canReverseToday =
    isEnabled && Boolean(today) && isDatePostable(today as string, booksLock?.lockDate)
  const hasBuildiumReversalOption = canReverseOriginal || canReverseToday

  useEffect(() => {
    if (!isOpen || !isEnabled) {
      return
    }

    if (preferToday && canReverseToday && today) {
      setSelectedReversalDate(today)
    } else if (canReverseOriginal) {
      setSelectedReversalDate(originalReversalDate)
    } else if (canReverseToday && today) {
      setSelectedReversalDate(today)
    } else if (!isBooksLockLoading) {
      setSelectedReversalDate(null)
    }
  }, [
    canReverseOriginal,
    canReverseToday,
    isBooksLockLoading,
    isEnabled,
    isOpen,
    originalReversalDate,
    preferToday,
    today
  ])

  return {
    booksLock,
    canReverseOriginal,
    canReverseToday,
    hasBuildiumReversalOption,
    isBooksLockError,
    isBooksLockLoading,
    originalReversalDate,
    selectedReversalDate,
    setSelectedReversalDate,
    today,
    resetReversalDateChoice: () => setSelectedReversalDate(null)
  }
}
